import { StyleSheet } from 'react-native';
import { useMemo } from 'react';
import { useThemeColors, useThemedStyles } from '@/hooks/residents/useThemeColors';

/**
 * Cork-board colors. Replaces the old static `CORK_BOARD_COLORS`, which was
 * frozen to the dark palette (it was the only feature palette without a themed
 * twin, and it lived here in the component layer rather than under
 * `src/constants/residents/`).
 *
 * It is NOT a key-for-key port of that const — 13 keys became 6, for three
 * reasons worth keeping:
 *
 * - `tagTextActive` is gone. It was a hardcoded `#FFFFFF` sitting on `tag.color`,
 *   which comes from the API and can be any color. White on a gold tag measures
 *   1.40:1 and on an amber tag 2.15:1 — unreadable today, in both themes. The
 *   ink now comes from `onColor(tagColor)` at the call site, where the fill is
 *   actually known.
 * - `announcementText` follows `semantic.text.link` instead of being pinned to
 *   resioBlueLight. That value was tuned for the dark card (5.12:1) but drops to
 *   3.03:1 on the white light-mode card. `text.link` keeps resioBlueLight in dark
 *   — byte-identical, so dark is untouched — and uses resioBlue in light (4.12:1,
 *   the app-wide link/CTA standard).
 * - `pinIcon`, `lockIcon`, `searchBackground`, `skeletonBase` and
 *   `skeletonHighlight` are gone: all five have zero consumers. Themed values
 *   were not invented for dead keys. (ThreadCardSkeleton already draws its own
 *   colors from `colors.card.*`.)
 *
 * `announcementBorder`, `announcementBg` and `defaultTagColor` stay fixed on
 * purpose: the first two are a brand blue border and an 8% brand tint that read
 * on either card, and the third is a fallback for missing tag data, not a
 * theme color.
 */
export function useCorkBoardColors() {
  const { semantic, components } = useThemeColors();
  return useMemo(
    () => ({
      cardBackground: components.card.background,
      cardBorder: components.card.border,
      announcementBorder: '#1D4ED8',
      announcementText: semantic.text.link,
      announcementBg: 'rgba(29, 78, 216, 0.08)',
      defaultTagColor: '#6B7280',
    }),
    [semantic, components],
  );
}

/** Shared cork-board styles. Replaces the old static `corkBoardStyles`, same keys. */
export function useCorkBoardStyles() {
  return useThemedStyles(({ semantic, components }) =>
    StyleSheet.create({
      screenContainer: {
        flex: 1,
        backgroundColor: semantic.background.primary,
      },
      feedContentContainer: {
        paddingBottom: 40,
      },
      sectionPadding: {
        paddingHorizontal: 16,
      },
      card: {
        backgroundColor: components.card.background,
        borderRadius: 14,
        borderWidth: 1,
        borderColor: components.card.border,
        padding: 16,
        gap: 10,
      },
      row: {
        flexDirection: 'row',
        alignItems: 'center',
      },
      textPrimary: {
        color: semantic.text.primary,
      },
      textSecondary: {
        color: semantic.text.secondary,
      },
      textMuted: {
        color: semantic.text.muted,
      },
      loadingContainer: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
        paddingVertical: 60,
      },
      avatar: {
        width: 28,
        height: 28,
        borderRadius: 14,
        backgroundColor: components.avatar.background,
        justifyContent: 'center',
        alignItems: 'center',
      },
      avatarText: {
        fontSize: 11,
        fontWeight: '600',
        color: components.avatar.text,
      },
    }),
  );
}
